import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag,
  Plus,
  RefreshCw,
  Unplug,
  Download,
  Trash2,
  Search,
  X,
  AlertCircle,
  CheckCircle2,
  Clock,
  Loader2,
  Store,
  Package,
  ExternalLink,
  Database,
  Zap,
  Copy,
  Check,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  integrationsService,
  Integration,
  IntegrationItem,
  ExportRecord,
} from '@/services/integrations.service';

type Tab = 'stores' | 'items' | 'exports';

export const IntegrationsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('stores');

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Integrations</h1>
          <p className="text-sm text-gray-500 mt-1">Connect stores, browse synced data, and export</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#1a1a2e] mb-6">
        <nav className="flex gap-8">
          {([
            { id: 'stores' as Tab, label: 'Stores', icon: Store },
            { id: 'items' as Tab, label: 'Items', icon: Package },
            { id: 'exports' as Tab, label: 'Exports', icon: Download },
          ]).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'stores' && <StoresTab />}
      {activeTab === 'items' && <ItemsTab />}
      {activeTab === 'exports' && <ExportsTab />}
    </div>
  );
};

// ============= Stores Tab =============

const StoresTab: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectPlatform, setConnectPlatform] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fetchIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await integrationsService.getIntegrations();
      setIntegrations(data);
    } catch {
      // toast handled by interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleSync = async (id: string) => {
    try {
      await integrationsService.syncIntegration(id);
      toast.success('Sync started');
      fetchIntegrations();
    } catch {
      // handled
    }
  };

  const handleDisconnect = async (id: string) => {
    if (!confirm('Disconnect this integration? All synced items will be deleted.')) return;
    try {
      await integrationsService.disconnectIntegration(id);
      toast.success('Integration disconnected');
      fetchIntegrations();
    } catch {
      // handled
    }
  };

  const totalProducts = integrations.reduce((sum, i) => sum + (i.item_counts?.product || 0), 0);
  const totalOrders = integrations.reduce((sum, i) => sum + (i.item_counts?.order || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-4">
          <p className="text-sm text-gray-500">Connected Stores</p>
          <p className="text-2xl font-bold text-gray-900">{integrations.length}</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-4">
          <p className="text-sm text-gray-500">Products Synced</p>
          <p className="text-2xl font-bold text-gray-900">{totalProducts.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-4">
          <p className="text-sm text-gray-500">Orders Synced</p>
          <p className="text-2xl font-bold text-gray-900">{totalOrders.toLocaleString()}</p>
        </div>
      </div>

      {/* Integration Cards */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Connected Integrations</h2>
        <button
          onClick={() => setShowConnectModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Connect Store
        </button>
      </div>

      {integrations.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
          <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations yet</h3>
          <p className="text-sm text-gray-500 mb-4">Connect your WooCommerce or Shopify store to sync products and orders.</p>
          <button
            onClick={() => setShowConnectModal(true)}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
          >
            Connect Store
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a2e] bg-gray-50">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Platform</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Products</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Orders</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Last Synced</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {integrations.map((integration) => (
                <tr key={integration.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
                        <PlatformIcon platform={integration.platform_type} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{integration.name}</p>
                        <p className="text-xs text-gray-400 font-mono flex items-center gap-1">
                          {integration.id}
                          <button
                            onClick={() => copyToClipboard(integration.id, 'Integration ID')}
                            className="p-0.5 hover:bg-gray-100 rounded transition-colors shrink-0"
                          >
                            {copiedId === integration.id ? (
                              <Check className="w-3 h-3 text-green-600" />
                            ) : (
                              <Copy className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                        </p>
                        {integration.sync_error && (
                          <p className="text-xs text-red-500 truncate mt-0.5" title={integration.sync_error}>
                            Error: {integration.sync_error}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                    {integration.platform_type.replace(/_/g, ' ')}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge status={integration.status} />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {(integration.item_counts?.product || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {(integration.item_counts?.order || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {integration.last_synced_at
                      ? new Date(integration.last_synced_at).toLocaleString()
                      : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {integration.platform_type !== 'custom_api' && (
                        <button
                          onClick={() => handleSync(integration.id)}
                          disabled={integration.status === 'syncing'}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                        >
                          <RefreshCw className={`w-3.5 h-3.5 ${integration.status === 'syncing' ? 'animate-spin' : ''}`} />
                          Sync Now
                        </button>
                      )}
                      <button
                        onClick={() => handleDisconnect(integration.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      >
                        <Unplug className="w-3.5 h-3.5" />
                        Disconnect
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Connect Modal - Platform Picker */}
      {showConnectModal && !connectPlatform && (
        <Modal onClose={() => setShowConnectModal(false)} title="Connect Store">
          <div className="space-y-3">
            {[
              { id: 'custom_api', name: 'Custom Data API', desc: 'Create a custom integration for data push' },
              { id: 'woocommerce', name: 'WooCommerce', desc: 'Connect your WordPress/WooCommerce store' },
              { id: 'shopify', name: 'Shopify', desc: 'Connect your Shopify store' },
            ].map((p) => (
              <button
                key={p.id}
                onClick={() => setConnectPlatform(p.id)}
                className="w-full flex items-center gap-4 p-4 border-2 border-[#1a1a2e] rounded-xl hover:border-amber-300 hover:bg-amber-50 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <PlatformIcon platform={p.id} />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{p.name}</p>
                  <p className="text-xs text-gray-500">{p.desc}</p>
                </div>
              </button>
            ))}
          </div>
        </Modal>
      )}

      {/* Custom API Connect Modal */}
      {connectPlatform === 'custom_api' && (
        <ConnectCustomApiModal
          onClose={() => { setConnectPlatform(null); setShowConnectModal(false); }}
          onSuccess={() => { setConnectPlatform(null); setShowConnectModal(false); fetchIntegrations(); }}
        />
      )}

      {/* WooCommerce Connect Modal */}
      {connectPlatform === 'woocommerce' && (
        <ConnectWooCommerceModal
          onClose={() => { setConnectPlatform(null); setShowConnectModal(false); }}
          onSuccess={() => { setConnectPlatform(null); setShowConnectModal(false); fetchIntegrations(); }}
        />
      )}

      {/* Shopify Connect Modal */}
      {connectPlatform === 'shopify' && (
        <ConnectShopifyModal
          onClose={() => { setConnectPlatform(null); setShowConnectModal(false); }}
          onSuccess={() => { setConnectPlatform(null); setShowConnectModal(false); fetchIntegrations(); }}
        />
      )}
    </>
  );
};

// ============= Items Tab =============

const ItemsTab: React.FC = () => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [selectedIntegration, setSelectedIntegration] = useState<string>('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [items, setItems] = useState<IntegrationItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    integrationsService.getIntegrations().then(setIntegrations).catch(() => {});
  }, []);

  const fetchItems = useCallback(async (cursor?: string) => {
    if (!selectedIntegration) return;
    setLoading(true);
    try {
      const result = await integrationsService.getItems(selectedIntegration, {
        type: selectedType || undefined,
        cursor,
        limit: 50,
      });
      if (cursor) {
        setItems((prev) => [...prev, ...result.items]);
      } else {
        setItems(result.items);
        setTotal(result.meta.total);
      }
      setNextCursor(result.meta.nextCursor);
    } catch {
      // handled
    } finally {
      setLoading(false);
    }
  }, [selectedIntegration, selectedType]);

  useEffect(() => {
    if (selectedIntegration) {
      fetchItems();
    } else {
      setItems([]);
      setTotal(0);
      setNextCursor(null);
    }
  }, [selectedIntegration, selectedType, fetchItems]);

  // Auto-select first integration
  useEffect(() => {
    if (integrations.length > 0 && !selectedIntegration) {
      setSelectedIntegration(integrations[0].id);
    }
  }, [integrations, selectedIntegration]);

  const filteredItems = search
    ? items.filter((item) => {
        const name = String(item.data?.name || item.external_id || '').toLowerCase();
        return name.includes(search.toLowerCase());
      })
    : items;

  return (
    <div>
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={selectedIntegration}
          onChange={(e) => { setSelectedIntegration(e.target.value); setItems([]); }}
          className="px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white"
        >
          <option value="">Select integration</option>
          {integrations.map((i) => (
            <option key={i.id} value={i.id}>{i.name}</option>
          ))}
        </select>

        <select
          value={selectedType}
          onChange={(e) => { setSelectedType(e.target.value); setItems([]); }}
          className="px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white"
        >
          <option value="">All types</option>
          <option value="product">Products</option>
          <option value="order">Orders</option>
        </select>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>

        {total > 0 && (
          <span className="text-sm text-gray-500">{total.toLocaleString()} total</span>
        )}
      </div>

      {/* Items Table */}
      {!selectedIntegration ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Select an integration to browse items</p>
        </div>
      ) : loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
          <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No items found</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a2e] bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Name / ID</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Details</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Last Synced</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.id} className="border-b border-[#1a1a2e] hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 truncate max-w-[250px]">
                      {String(item.data?.name || item.external_id)}
                    </p>
                    <p className="text-xs text-gray-400">{item.external_id}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                      {item.item_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {item.item_type === 'product' && item.data?.price != null && (
                      <span>${String(item.data.price)}</span>
                    )}
                    {item.item_type === 'order' && item.data?.total != null && (
                      <span>{String(item.data.currency ?? '$')}{String(item.data.total)} - {String(item.data.status ?? '')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {new Date(item.last_synced_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {nextCursor && (
            <div className="p-4 text-center border-t border-[#1a1a2e]">
              <button
                onClick={() => fetchItems(nextCursor)}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-amber-600 hover:text-amber-700 disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  'Load More'
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============= Exports Tab =============

const ExportsTab: React.FC = () => {
  const [exports, setExports] = useState<ExportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const pollingRef = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  const fetchExports = useCallback(async () => {
    try {
      const data = await integrationsService.getExports();
      setExports(data);
      return data;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  // Poll active exports
  useEffect(() => {
    const activeExports = exports.filter((e) => e.status === 'pending' || e.status === 'processing');

    for (const exp of activeExports) {
      if (!pollingRef.current[exp.id]) {
        pollingRef.current[exp.id] = setInterval(async () => {
          try {
            const updated = await integrationsService.getExportStatus(exp.id);
            setExports((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
            if (updated.status === 'completed' || updated.status === 'failed' || updated.status === 'cancelled') {
              clearInterval(pollingRef.current[exp.id]);
              delete pollingRef.current[exp.id];
              if (updated.status === 'completed') {
                toast.success('Export completed!');
              } else if (updated.status === 'failed') {
                toast.error('Export failed: ' + (updated.error_message || 'Unknown error'));
              }
            }
          } catch {
            // ignore poll errors
          }
        }, 3000);
      }
    }

    return () => {
      // cleanup removed exports
      for (const id of Object.keys(pollingRef.current)) {
        if (!activeExports.find((e) => e.id === id)) {
          clearInterval(pollingRef.current[id]);
          delete pollingRef.current[id];
        }
      }
    };
  }, [exports]);

  // Cleanup all polling on unmount
  useEffect(() => {
    return () => {
      for (const id of Object.keys(pollingRef.current)) {
        clearInterval(pollingRef.current[id]);
      }
    };
  }, []);

  const handleDownload = async (exp: ExportRecord) => {
    try {
      const blob = await integrationsService.downloadExport(exp.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = exp.file_key?.split('/').pop()?.replace('.gz', '') || `export.${exp.format}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this export?')) return;
    try {
      await integrationsService.deleteExport(id);
      toast.success('Export deleted');
      setExports((prev) => prev.filter((e) => e.id !== id));
    } catch {
      // handled
    }
  };

  const handleCreated = (newExport: ExportRecord) => {
    setExports((prev) => [newExport, ...prev]);
    setShowCreateModal(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Export History</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Export
        </button>
      </div>

      {exports.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] p-12 text-center">
          <Download className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No exports yet</h3>
          <p className="text-sm text-gray-500 mb-4">Export your data as CSV, JSON, or JSONL files.</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
          >
            Create Export
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#1a1a2e] bg-gray-50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Entity Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Format</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Rows</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {exports.map((exp) => (
                <tr key={exp.id} className="border-b border-[#1a1a2e] hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                    {exp.entity_type.replace(/_/g, ' ')}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 uppercase">
                      {exp.format}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <ExportStatusBadge export={exp} />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {exp.status === 'processing' ? (
                      <span>{exp.processed_rows.toLocaleString()} / {exp.total_rows.toLocaleString()}</span>
                    ) : (
                      <span>{exp.total_rows.toLocaleString()}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {exp.file_size ? formatFileSize(exp.file_size) : '-'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {new Date(exp.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {exp.status === 'completed' && (
                        <button
                          onClick={() => handleDownload(exp)}
                          className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(exp.id)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <CreateExportModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

// ============= Modals =============

const Modal: React.FC<{ onClose: () => void; title: string; children: React.ReactNode }> = ({
  onClose,
  title,
  children,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="bg-white rounded-xl shadow-[5px_5px_0px_#1a1a2e] w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between p-5 border-b border-[#1a1a2e]">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const ConnectWooCommerceModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({
  onClose,
  onSuccess,
}) => {
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [name, setName] = useState('');
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (!storeUrl || !consumerKey || !consumerSecret) {
      toast.error('Store URL, Consumer Key, and Consumer Secret are required');
      return;
    }
    setConnecting(true);
    try {
      await integrationsService.connectStore('woocommerce', {
        store_url: storeUrl,
        consumer_key: consumerKey,
        consumer_secret: consumerSecret,
      }, name || undefined);
      toast.success('WooCommerce store connected! Sync started.');
      onSuccess();
    } catch {
      // handled by interceptor
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Connect WooCommerce">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Store URL</label>
          <input
            type="url"
            placeholder="https://mystore.com"
            value={storeUrl}
            onChange={(e) => setStoreUrl(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Consumer Key</label>
          <input
            type="password"
            placeholder="ck_..."
            value={consumerKey}
            onChange={(e) => setConsumerKey(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Consumer Secret</label>
          <input
            type="password"
            placeholder="cs_..."
            value={consumerSecret}
            onChange={(e) => setConsumerSecret(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Store Name (optional)</label>
          <input
            type="text"
            placeholder="My Store"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
          {connecting ? 'Connecting...' : 'Connect'}
        </button>
      </div>
    </Modal>
  );
};

const ConnectShopifyModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({
  onClose,
  onSuccess,
}) => {
  const [storeUrl, setStoreUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [name, setName] = useState('');
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (!storeUrl || !accessToken) {
      toast.error('Store URL and Access Token are required');
      return;
    }
    setConnecting(true);
    try {
      await integrationsService.connectStore('shopify', {
        store_url: storeUrl,
        access_token: accessToken,
      }, name || undefined);
      toast.success('Shopify store connected! Sync started.');
      onSuccess();
    } catch {
      // handled
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Connect Shopify">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Store URL</label>
          <input
            type="url"
            placeholder="mystore.myshopify.com"
            value={storeUrl}
            onChange={(e) => setStoreUrl(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
          <input
            type="password"
            placeholder="shpat_..."
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Store Name (optional)</label>
          <input
            type="text"
            placeholder="My Shopify Store"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <button
          onClick={handleConnect}
          disabled={connecting}
          className="w-full py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
          {connecting ? 'Connecting...' : 'Connect'}
        </button>
      </div>
    </Modal>
  );
};

const CreateExportModal: React.FC<{
  onClose: () => void;
  onCreated: (exp: ExportRecord) => void;
}> = ({ onClose, onCreated }) => {
  const [entityType, setEntityType] = useState('integration_items');
  const [format, setFormat] = useState('csv');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [integrationId, setIntegrationId] = useState('');
  const [itemType, setItemType] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [integrations, setIntegrations] = useState<Integration[]>([]);

  useEffect(() => {
    integrationsService.getIntegrations().then(setIntegrations).catch(() => {});
  }, []);

  const entityTypes = [
    { value: 'integration_items', label: 'Integration Items' },
    { value: 'conversations', label: 'Conversations' },
    { value: 'messages', label: 'Messages' },
    { value: 'customers', label: 'Customers' },
    { value: 'agents', label: 'Agents' },
    { value: 'agent_analytics', label: 'Agent Analytics' },
    { value: 'escalations', label: 'Escalations' },
    { value: 'generated_documents', label: 'Generated Documents' },
    { value: 'notifications', label: 'Notifications' },
  ];

  const handleCreate = async () => {
    setCreating(true);
    try {
      const filters: Record<string, unknown> = {};
      if (dateFrom) filters.date_from = dateFrom;
      if (dateTo) filters.date_to = dateTo;
      if (integrationId) filters.integration_id = integrationId;
      if (itemType) filters.item_type = itemType;
      if (statusFilter) filters.status = statusFilter;

      const exp = await integrationsService.createExport({
        entity_type: entityType,
        format,
        filters,
      });
      toast.success('Export started');
      onCreated(exp);
    } catch {
      // handled
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal onClose={onClose} title="New Export">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white"
          >
            {entityTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Format</label>
          <div className="flex gap-2">
            {['csv', 'json', 'jsonl'].map((f) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  format === f
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Date filters */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Entity-specific filters */}
        {entityType === 'integration_items' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Integration</label>
              <select
                value={integrationId}
                onChange={(e) => setIntegrationId(e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white"
              >
                <option value="">All integrations</option>
                {integrations.map((i) => (
                  <option key={i.id} value={i.id}>{i.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
              <select
                value={itemType}
                onChange={(e) => setItemType(e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white"
              >
                <option value="">All types</option>
                <option value="product">Products</option>
                <option value="order">Orders</option>
              </select>
            </div>
          </>
        )}

        {(entityType === 'conversations' || entityType === 'escalations') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <input
              type="text"
              placeholder="e.g. active, resolved, open"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
            />
          </div>
        )}

        <button
          onClick={handleCreate}
          disabled={creating}
          className="w-full py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {creating && <Loader2 className="w-4 h-4 animate-spin" />}
          {creating ? 'Starting...' : 'Start Export'}
        </button>
      </div>
    </Modal>
  );
};

const ConnectCustomApiModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    if (!name) {
      toast.error('Integration name is required');
      return;
    }
    setConnecting(true);
    try {
      await integrationsService.createCustomIntegration(name, description || undefined);
      toast.success('Custom integration created! You can now push data to it.');
      onSuccess();
    } catch {
      // handled by interceptor
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Create Custom Data Integration">
      <div className="space-y-4">
        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-indigo-600" />
            <p className="text-sm text-indigo-700">
              Custom integrations let you push data via the Data API. A linked knowledge base will be created automatically.
            </p>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Integration Name</label>
          <input
            type="text"
            placeholder="e.g. Product Catalog, FAQ Database"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
          <textarea
            placeholder="Describe the type of data you'll push to this integration"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm resize-none"
          />
        </div>
        <button
          onClick={handleConnect}
          disabled={connecting || !name}
          className="w-full py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {connecting && <Loader2 className="w-4 h-4 animate-spin" />}
          {connecting ? 'Creating...' : 'Create Integration'}
        </button>
      </div>
    </Modal>
  );
};

// ============= Shared Components =============

const PlatformIcon: React.FC<{ platform: string }> = ({ platform }) => {
  switch (platform) {
    case 'woocommerce':
      return <ShoppingBag className="w-5 h-5 text-purple-600" />;
    case 'shopify':
      return <Store className="w-5 h-5 text-green-600" />;
    case 'custom_api':
      return <Database className="w-5 h-5 text-indigo-600" />;
    default:
      return <ExternalLink className="w-5 h-5 text-gray-600" />;
  }
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const styles: Record<string, string> = {
    connected: 'bg-green-100 text-green-700',
    syncing: 'bg-amber-100 text-amber-700',
    error: 'bg-red-100 text-red-700',
    disconnected: 'bg-gray-100 text-gray-600',
  };

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.disconnected}`}>
      {status === 'syncing' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status === 'connected' && <CheckCircle2 className="w-3 h-3" />}
      {status === 'error' && <AlertCircle className="w-3 h-3" />}
      <span className="capitalize">{status}</span>
    </span>
  );
};

const ExportStatusBadge: React.FC<{ export: ExportRecord }> = ({ export: exp }) => {
  if (exp.status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-amber-600">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  }

  if (exp.status === 'processing') {
    const pct = exp.total_rows > 0 ? Math.round((exp.processed_rows / exp.total_rows) * 100) : 0;
    return (
      <div className="flex items-center gap-2">
        <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-amber-500 rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="text-xs text-amber-600">{pct}%</span>
      </div>
    );
  }

  if (exp.status === 'completed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-green-600">
        <CheckCircle2 className="w-3 h-3" />
        Completed
      </span>
    );
  }

  if (exp.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-red-600" title={exp.error_message}>
        <AlertCircle className="w-3 h-3" />
        Failed
      </span>
    );
  }

  return (
    <span className="text-xs text-gray-500 capitalize">{exp.status}</span>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
